import {
  Flex,
  Banner,
  LabelValueText as LabelValueTextComponent,
  Text,
} from '@components';
import { LabelValueTextProps } from '@components/LabelValueText/LabelValueText';
import { Config } from '@config/config';
import { User } from '@features/api/response/User';
import {
  PaymentDetails,
  UserTransaction,
} from '@features/api/response/UserTransactions';
import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

export interface PaymentInstructionsProps {
  transaction: UserTransaction;
  user?: User;
}

const PaymentInstructions = ({
  transaction,
  user,
}: PaymentInstructionsProps) => {
  const intl = useIntl();
  const theme = useTheme();

  if (!transaction.paymentDetails) return <></>;

  const LabelValueText = ({ ...props }: LabelValueTextProps) => (
    <LabelValueTextComponent {...props} hideButtonBorder />
  );

  const renderBankDetails = (paymentDetails: PaymentDetails) => {
    return (
      <>
        <LabelValueText
          label={intl.formatMessage({
            id: 'investment.bankDetails.accountHolderName',
          })}
          value={paymentDetails.accountHolderName}
        />
        <LabelValueText
          label={intl.formatMessage({
            id: 'investment.bankDetails.bankName',
          })}
          value={paymentDetails.bankName}
        />
        <LabelValueText
          label={intl.formatMessage({
            id: 'investment.bankDetails.bankLocation',
          })}
          value={paymentDetails.bankLocation}
        />
        {paymentDetails.iban ? (
          <LabelValueText
            label={intl.formatMessage({
              id: 'investment.bankDetails.iban',
            })}
            value={paymentDetails.iban}
          />
        ) : null}
        {paymentDetails.bankAccountNumber && paymentDetails.swiftOrBic ? (
          <>
            <LabelValueText
              label={intl.formatMessage({
                id: 'investment.bankDetails.bankAccountNumber',
              })}
              value={paymentDetails.bankAccountNumber}
            />
            <LabelValueText
              label={intl.formatMessage({
                id: 'investment.bankDetails.swiftAndBic',
              })}
              value={paymentDetails.swiftOrBic}
            />
          </>
        ) : null}
      </>
    );
  };

  return (
    <Flex flexDirection='column' gap={10}>
      <Banner color='warning'>
        <Box color={theme.palette.primary.main} padding={4}>
          <Text variant='h6' fontWeight='fontWeightBold'>
            <FormattedMessage id='portfolio.transactions.paymentInstructions.smoothInvestment' />
          </Text>
          <ul
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(5),
            }}
          >
            <li>
              <Text variant='h6' fontWeight='fontWeightBold'>
                <FormattedMessage id='portfolio.transactions.paymentInstructions.transferFullAmount' />
              </Text>
            </li>
            <li>
              <Text variant='h6' fontWeight='fontWeightBold'>
                <FormattedMessage id='portfolio.transactions.paymentInstructions.onlyAcceptFromDetailsBelow' />
              </Text>
            </li>
            <li>
              <Text variant='h6' fontWeight='fontWeightBold'>
                <FormattedMessage
                  id='portfolio.transactions.paymentInstructions.onlyAcceptTransactionCurrency'
                  values={{
                    currency: transaction.currency ?? Config.currency.code,
                  }}
                />
              </Text>
            </li>
          </ul>
        </Box>
      </Banner>
      <Flex flexDirection='column' gap={6} mt={4}>
        <Text variant='h6' fontWeight='fontWeightBold'>
          <FormattedMessage id='portfolio.transactions.paymentInstructions.paymentDetails' />
        </Text>
        <LabelValueText
          label={intl.formatMessage({
            id: 'portfolio.transactions.paymentInstructions.transferAmount',
          })}
          value={intl.formatNumber(transaction?.paymentDetails?.amount ?? 0, {
            style: 'currency',
            currency: transaction.currency ?? Config.currency.code,
            minimumFractionDigits: 2,
          })}
        />
        <LabelValueText
          label={intl.formatMessage({
            id: 'portfolio.transactions.paymentInstructions.currency',
          })}
          value={transaction.currency ?? Config.currency.code}
        />
      </Flex>
      <Flex flexDirection='column' gap={6}>
        <Text variant='h6' fontWeight='fontWeightBold'>
          <FormattedMessage id='portfolio.transactions.paymentInstructions.yourDetails' />
        </Text>

        {renderBankDetails(transaction.paymentDetails.originatorDetails)}
      </Flex>
      <Flex flexDirection='column' gap={6}>
        <Text variant='h6' fontWeight='fontWeightBold'>
          <FormattedMessage id='portfolio.transactions.paymentInstructions.swiseDetails' />
        </Text>
        {renderBankDetails(transaction.paymentDetails.receiverDetails)}
      </Flex>
    </Flex>
  );
};

export default PaymentInstructions;
